import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MetricCard } from '@/components/MetricCard';
import { PieChart, ChartContainer } from '@/components/charts';
import { useCallsOutcomeStats } from '../hooks';
import type { PeriodFilter } from '../api';
import {
  RefreshCw, Phone, PhoneCall, PhoneOff, CheckCircle, Clock,
  ArrowLeft, TrendingUp, XCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

type Period = PeriodFilter;

export default function CallsAnalyticsPage() {
  const { t } = useTranslation('intelligence');
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('week');

  const { data: stats, isLoading: statsLoading, refetch, isFetching } = useCallsOutcomeStats(period);

  const periods: { value: Period; label: string }[] = [
    { value: 'day', label: t('communications.calls.periods.day', 'Today') },
    { value: 'week', label: t('communications.calls.periods.week', 'This Week') },
    { value: 'month', label: t('communications.calls.periods.month', 'This Month') },
    { value: 'quarter', label: t('communications.calls.periods.quarter', 'This Quarter') },
  ];

  const formatNumber = (num: number | undefined) => {
    if (num === undefined || num === null) return '--';
    return num.toLocaleString();
  };

  const formatPercent = (value: number | undefined) => {
    if (value === undefined || value === null) return '--';
    return `${(Math.round(value * 10) / 10)}%`;
  };

  const formatDuration = (seconds: number | undefined) => {
    if (seconds === undefined || seconds === null) return '--';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const minutes = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const goBack = () => {
    navigate('/intelligence/communications');
  };

  // Outcome breakdown chart data
  const outcomeChartData = [
    { name: t('communications.calls.outcomes.completed', 'Completed'), value: stats?.completed || 0, color: '#27ae60' },
    { name: t('communications.calls.outcomes.busy', 'Busy'), value: stats?.busy || 0, color: '#f39c12' },
    { name: t('communications.calls.outcomes.noAnswer', 'No Answer'), value: stats?.no_answer || 0, color: '#95a5a6' },
    { name: t('communications.calls.outcomes.failed', 'Failed'), value: stats?.failed || 0, color: '#e74c3c' },
  ].filter(d => d.value > 0);

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={goBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('communications.calls.backToOverview', 'Back to Overview')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Phone className="h-6 w-6 text-orange-600" />
              {t('communications.calls.title', 'Calls Analytics')}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex rounded-lg border bg-white overflow-hidden">
            {periods.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  period === p.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-gray-600 hover:bg-gray-100'
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
            {t('common:refresh', 'Refresh')}
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <MetricCard
          title={t('communications.calls.cards.totalCalls', 'Total Calls')}
          value={stats?.total_calls}
          icon={<Phone className="h-5 w-5" />}
          color="orange"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.calls.cards.answered', 'Answered')}
          value={stats?.answered}
          icon={<PhoneCall className="h-5 w-5" />}
          color="green"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.calls.cards.completed', 'Completed')}
          value={stats?.completed}
          icon={<CheckCircle className="h-5 w-5" />}
          color="blue"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.calls.cards.busy', 'Busy')}
          value={stats?.busy}
          icon={<PhoneOff className="h-5 w-5" />}
          color="orange"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.calls.cards.noAnswer', 'No Answer')}
          value={stats?.no_answer}
          icon={<PhoneOff className="h-5 w-5" />}
          color="purple"
          loading={statsLoading}
        />
        <MetricCard
          title={t('communications.calls.cards.failed', 'Failed')}
          value={stats?.failed}
          icon={<XCircle className="h-5 w-5" />}
          color="red"
          loading={statsLoading}
        />
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.calls.cards.answerRate', 'Answer Rate')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-green-600">
                    {formatPercent(stats?.answer_rate)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase">{t('communications.calls.cards.avgDuration', 'Avg. Duration')}</p>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16 mt-1" />
                ) : (
                  <p className="text-xl font-bold text-blue-600">
                    {formatDuration(stats?.avg_talk_duration_seconds)}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Outcome Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.calls.sections.outcomes', 'Call Outcomes')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer loading={statsLoading} height={350}>
              {outcomeChartData.length > 0 ? (
                <PieChart
                  data={outcomeChartData}
                  height={320}
                  showLegend
                  innerRadius={70}
                />
              ) : (
                <div className="flex items-center justify-center h-[320px] text-gray-400">
                  <div className="text-center">
                    <Phone className="h-12 w-12 mx-auto mb-2" />
                    <p>{t('communications.calls.noData', 'No call data available')}</p>
                  </div>
                </div>
              )}
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <Card>
          <CardHeader>
            <CardTitle>{t('communications.calls.sections.summary', 'Call Summary')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Total Calls */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-500" />
                  <span className="font-medium">{t('communications.calls.cards.totalCalls', 'Total Calls')}</span>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <span className="text-xl font-bold">{formatNumber(stats?.total_calls)}</span>
                )}
              </div>

              {/* Answered Breakdown */}
              <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <PhoneCall className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">{t('communications.calls.cards.answered', 'Answered')}</span>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <div className="text-right">
                    <span className="text-xl font-bold text-green-600">{formatNumber(stats?.answered)}</span>
                    <span className="text-sm text-green-600 ml-2">({formatPercent(stats?.answer_rate)})</span>
                  </div>
                )}
              </div>

              {/* Completed */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  <span className="font-medium text-blue-800">{t('communications.calls.cards.completed', 'Completed')}</span>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <span className="text-xl font-bold text-blue-600">{formatNumber(stats?.completed)}</span>
                )}
              </div>

              {/* Busy */}
              <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <PhoneOff className="h-5 w-5 text-yellow-600" />
                  <span className="font-medium text-yellow-800">{t('communications.calls.cards.busy', 'Busy')}</span>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <span className="text-xl font-bold text-yellow-600">{formatNumber(stats?.busy)}</span>
                )}
              </div>

              {/* No Answer */}
              <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg">
                <div className="flex items-center gap-3">
                  <PhoneOff className="h-5 w-5 text-gray-500" />
                  <span className="font-medium text-gray-700">{t('communications.calls.cards.noAnswer', 'No Answer')}</span>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <span className="text-xl font-bold text-gray-600">{formatNumber(stats?.no_answer)}</span>
                )}
              </div>

              {/* Failed */}
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="font-medium text-red-800">{t('communications.calls.cards.failed', 'Failed')}</span>
                </div>
                {statsLoading ? (
                  <Skeleton className="h-6 w-16" />
                ) : (
                  <span className="text-xl font-bold text-red-600">{formatNumber(stats?.failed)}</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
